import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import UtilContext from '../../components/UtilContext';
import AuthContext from '../../components/AuthContext';
import { LocaleContext } from '../../components/LocaleContext';
import { Checkbox } from 'primereact/checkbox';
import { Select } from 'antd';
import countryList from 'react-select-country-list';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/sk';
import 'i18n-iso-countries/langs/en';
import 'i18n-iso-countries/langs/de';
import { createAsyncNavigateHandlers } from '../../utils/asyncArrayNavigate';
import { customerClientTypeDisplayLabel } from '../../utils/customerClientTypeDisplayLabel';
import { activeCrmTraderUsers, isActiveCrmTraderToday } from '../../utils/crmTraders';
import CustomerBusinessStatusPanel from '../../components/crm/CustomerBusinessStatusPanel';
import { SaveIcon, AddIcon, CopyIcon, VerticalLineIcon, SaveAndCloseIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon } from '../svg/svgIcons';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';

const PAGE_ADD_KEY = 'CustomerCentralDatabaseAdd';
const MASK_ADD = 'cust012';
countries.registerLocale(require('i18n-iso-countries/langs/sk'));
countries.registerLocale(require('i18n-iso-countries/langs/en'));
countries.registerLocale(require('i18n-iso-countries/langs/de'));

function isApiSuccess(resp) {
  const status = Number(resp?.status ?? 0);
  return status >= 200 && status < 300;
}

function unwrapApiRow(resp) {
  if (!isApiSuccess(resp)) return null;
  const row = resp?.data;
  if (row && typeof row === 'object' && row.data && typeof row.data === 'object') {
    return row.data;
  }
  return row && typeof row === 'object' ? row : null;
}

function apiErrorMessage(resp, fallback, t = null) {
  const raw = String(resp?.message || resp?.error || '');
  if (typeof t === 'function') {
    if (raw === 'Trader is not currently valid.') {
      return t('CustomerCentralDatabaseAdd.Toast.TraderNotCurrentlyValid');
    }
    if (raw === 'Customer is already occupied by another active trader.') {
      return t('CustomerCentralDatabaseAdd.Toast.OccupiedByAnotherTrader');
    }
  }
  return raw || String(fallback);
}

function normalizeInitialValues(companyRow) {
  const types = Array.isArray(companyRow?.types) ? companyRow.types : [];
  const statusByType = companyRow?.partner_status_by_type && typeof companyRow.partner_status_by_type === 'object'
    ? companyRow.partner_status_by_type
    : {};
  const partyMode = companyRow?.party_mode
    || statusByType?.party_mode
    || (types.includes('customer') ? 'customer' : 'client');
  const selectedCustomerClientType = partyMode === 'client'
    ? (companyRow?.client_type || statusByType?.client || companyRow?.customer_type || statusByType?.customer || '')
    : (companyRow?.customer_type || statusByType?.customer || companyRow?.client_type || statusByType?.client || '');
  return {
    id: companyRow?.id || null,
    name: companyRow?.name || '',
    defined_id: companyRow?.defined_id || '',
    address: companyRow?.address || '',
    tel_number: companyRow?.tel_number || '',
    email: companyRow?.email || '',
    web_address: companyRow?.web_address || '',
    contact_person: companyRow?.contact_person || '',
    description: companyRow?.description || '',
    country: companyRow?.country || '',
    types,
    party_mode: partyMode,
    customer_type: companyRow?.customer_type || statusByType?.customer || '',
    client_type: companyRow?.client_type || statusByType?.client || '',
    customer_client_type: selectedCustomerClientType,
    customer_state: companyRow?.customer_state || 'free',
    trader_id: companyRow?.trader_id || null,
    trader_name: companyRow?.trader_name || '-',
    execution_user_id: companyRow?.execution_user_id || null,
    execution_user_name: companyRow?.execution_user_name || '',
    deadline: companyRow?.deadline || null,
    deadline_reminder_comment_id: companyRow?.deadline_reminder_comment_id ?? null,
    created_by_name: companyRow?.created_by_name || '',
    updated_by_name: companyRow?.updated_by_name || '',
    created_at: companyRow?.created_at || null,
    updated_at: companyRow?.updated_at || null,
    comments: Array.isArray(companyRow?.comments) ? companyRow.comments : [],
    mentions: Array.isArray(companyRow?.mentions) ? companyRow.mentions : [],
    supervisor_user_ids: Array.isArray(companyRow?.supervisor_user_ids) ? companyRow.supervisor_user_ids : [],
  };
}

export default function CustomerCentralDatabaseAdd({ companyRow, type = 'add', navigationContext = null }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
  const {
    toast,
    openFavoriteDialog,
    setFastView,
    setDialogText,
    setVisible,
  } = useContext(UtilContext);
  const [formType, setFormType] = useState(type);
  const [closeTab, setCloseTab] = useState(false);
  const [detailActivityLoading, setDetailActivityLoading] = useState(false);
  const [allUsersForMentions, setAllUsersForMentions] = useState([]);
  const canSelectAnyTrader = !!user?.permissions?.includes('access customer_management-complete-list');
  const submitRef = useRef(null);
  const customerClientTypeOptions = useMemo(() => ([
    { value: 'new_client', label: t('CustomerTypeOptions.NewClient') },
    { value: 'potential_client', label: t('CustomerTypeOptions.PotentialClient') },
    { value: 'verified_client', label: t('CustomerTypeOptions.VerifiedClient') },
    { value: 'inquiring_client', label: t('CustomerTypeOptions.InquiringClient') },
    { value: 'occasional_customer', label: t('CustomerTypeOptions.OccasionalCustomer') },
    { value: 'regular_customer', label: t('CustomerTypeOptions.RegularCustomer') },
    { value: 'strategic_customer', label: t('CustomerTypeOptions.StrategicCustomer') },
    { value: 'lost_customer', label: t('CustomerTypeOptions.LostCustomer') },
  ]), [t]);
  const clientTypeValues = useMemo(() => new Set([
    'new_client',
    'potential_client',
    'verified_client',
    'inquiring_client',
  ]), []);
  const customerTypeValues = useMemo(() => new Set([
    'occasional_customer',
    'regular_customer',
    'strategic_customer',
    'lost_customer',
  ]), []);
  const clientTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => clientTypeValues.has(opt.value)),
    [customerClientTypeOptions, clientTypeValues]
  );
  const customerTypeOptions = useMemo(
    () => customerClientTypeOptions.filter((opt) => customerTypeValues.has(opt.value)),
    [customerClientTypeOptions, customerTypeValues]
  );
  const countryOptions = useMemo(() => (
    countryList().getData().map((country) => ({
      value: country.value,
      label: countries.getName(country.value, locale === 'de' ? 'de' : (locale === 'en' || locale === 'gb') ? 'en' : 'sk') || country.label,
    }))
  ), [locale]);

  const traders = useMemo(() => {
    const rawTraders = Array.isArray(state?.crm_traders) ? state.crm_traders : [];
    const active = rawTraders
      .filter((r) => isActiveCrmTraderToday(r))
      .map((r) => ({
        id: r?.user_id,
        name: r?.user?.employee?.name || r?.user?.username || `#${r?.user_id}`,
      }));
    return canSelectAnyTrader
      ? active
      : active.filter((traderOption) => Number(traderOption.id) === Number(user?.id));
  }, [canSelectAnyTrader, state?.crm_traders, user?.id]);

  useEffect(() => {
    setFormType(type);
  }, [type]);

  useEffect(() => {
    let cancelled = false;
    const fromStore = state?.users;
    if (Array.isArray(fromStore) && fromStore.length > 0) {
      setAllUsersForMentions(fromStore);
      return () => { cancelled = true; };
    }
    getRequest('/api/users')
      .then((resp) => {
        if (!cancelled) setAllUsersForMentions(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch(() => {
        if (!cancelled) setAllUsersForMentions([]);
      });
    return () => { cancelled = true; };
  }, [state.users]);

  const canAdd = !!user?.permissions?.includes('add customer_management');
  const canEdit = !!user?.permissions?.includes('edit customer_management');

  const fetchCentralDetail = useCallback(async (companyId) => {
    const resp = await getRequest(`/api/customer_evaluations/central_db/${companyId}`);
    if (!isApiSuccess(resp)) {
      throw new Error(apiErrorMessage(resp, t('Common.LoadFailed'), t));
    }
    return unwrapApiRow(resp);
  }, [t]);

  const assignTraderRequest = useCallback(async (companyId, traderId) => {
    const resp = await postRequest(`/api/customer_evaluations/central_db/${companyId}/assign_trader`, {
      trader_id: traderId,
      force: canSelectAnyTrader,
    }, true);
    const row = unwrapApiRow(resp);
    if (row) return row;
    throw new Error(apiErrorMessage(resp, t('CustomerCentralDatabaseAdd.Toast.AssignFailed'), t));
  }, [canSelectAnyTrader, t]);

  const unassignTraderRequest = useCallback(async (companyId) => {
    const resp = await postRequest(`/api/customer_evaluations/central_db/${companyId}/unassign_trader`, {}, true);
    const row = unwrapApiRow(resp);
    if (row) return row;
    throw new Error(apiErrorMessage(resp, t('CustomerCentralDatabaseAdd.Toast.UnassignFailed'), t));
  }, [t]);

  const formik = useFormik({
    initialValues: normalizeInitialValues(companyRow),
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.name || !String(values.name).trim()) errors.name = t('Common.Required');
      return errors;
    },
    onSubmit: async (values) => {
      setDialogText?.(t('Common.Saving'));
      setVisible?.(true);
      try {
        const payload = {
          customer_type: null,
          client_type: null,
          name: values.name,
          defined_id: values.defined_id || null,
          address: values.address || null,
          tel_number: values.tel_number || null,
          email: values.email || null,
          web_address: values.web_address || null,
          contact_person: values.contact_person || null,
          description: values.description || null,
          country: values.country || null,
          types: values.types || [],
          party_mode: values.party_mode || 'none',
        };
        let saved = null;
        const selectedTraderId = Number(values?.trader_id || 0) || null;
        if (formType === 'add') {
          const resp = await postRequest('/api/customer_evaluations/central_db', payload, true);
          saved = unwrapApiRow(resp);
          if (!saved?.id) {
            throw new Error(apiErrorMessage(resp, t('ServiceTask.SaveFailed'), t));
          }
          setFormType('edit');
        } else if (values.id) {
          const resp = await postRequest(`/api/customer_evaluations/central_db/update/${values.id}`, payload, true);
          saved = unwrapApiRow(resp);
          if (!saved?.id) {
            throw new Error(apiErrorMessage(resp, t('ServiceTask.SaveFailed'), t));
          }
        }
        const successDetail = formType === 'add'
          ? t('CustomerCentralDatabaseAdd.Toast.Saved')
          : t('CustomerCentralDatabaseAdd.Toast.Updated');
        const savedTraderId = Number(saved?.trader_id || 0) || null;
        let assignWarning = null;
        if (saved?.id && canEdit && selectedTraderId !== savedTraderId) {
          try {
            saved = selectedTraderId
              ? await assignTraderRequest(saved.id, selectedTraderId)
              : await unassignTraderRequest(saved.id);
          } catch (assignErr) {
            assignWarning = assignErr?.message || t('CustomerCentralDatabaseAdd.Toast.AssignFailed');
            toast?.current?.show({
              severity: 'warn',
              summary: t('Common.Warning'),
              detail: assignWarning,
              life: 4000,
            });
          }
        }
        if (saved?.id) {
          let detailRow = saved;
          try {
            detailRow = await fetchCentralDetail(saved.id) || saved;
          } catch (loadErr) {
            if (!assignWarning) {
              toast?.current?.show({
                severity: 'warn',
                summary: t('Common.Warning'),
                detail: loadErr?.message || t('Common.LoadFailed'),
                life: 3500,
              });
            }
          }
          const nextValues = normalizeInitialValues(detailRow);
          formik.setValues(nextValues);
          window.dispatchEvent(new CustomEvent('crm-central-db:changed', { detail: detailRow }));
        }
        if (!assignWarning) {
          toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), detail: successDetail, life: 2500 });
        }
        if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
      } catch (e) {
        toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('ServiceTask.SaveFailed'), life: 3500 });
      } finally {
        setVisible?.(false);
        setCloseTab(false);
      }
    },
  });

  const mergeDetailIntoFormik = (row) => {
    if (!row) return;
    formik.setValues(normalizeInitialValues(row));
  };

  const editCustomerManagement = !!user?.permissions?.includes('edit customer_management');

  const usersForMentions = useMemo(() => {
    const extraIds = [
      formik.values?.execution_user_id,
      ...(formik.values?.mentions || []).map((m) => m?.id),
    ];
    return activeCrmTraderUsers(state?.crm_traders || [], allUsersForMentions, extraIds);
  }, [allUsersForMentions, formik.values?.execution_user_id, formik.values?.mentions, state?.crm_traders]);

  const canManageInterestedUsers = useMemo(() => {
    const uid = user?.id != null ? Number(user.id) : 0;
    const tid = formik.values?.trader_id != null ? Number(formik.values.trader_id) : 0;
    const perms = user?.permissions || [];
    if (uid > 0 && tid > 0 && uid === tid) return true;
    if (perms.includes('access customer_management-complete-list')) return true;
    const mentionIds = new Set((formik.values?.mentions || []).map((m) => Number(m?.id)));
    if (uid > 0 && mentionIds.has(uid)) return true;
    if (tid === 0 && editCustomerManagement && formType === 'edit' && formik.values?.id) return true;
    return false;
  }, [user, formik.values?.trader_id, formik.values?.mentions, formik.values?.id, editCustomerManagement, formType]);

  const canRemoveInterestedUsers = useMemo(() => {
    const uid = user?.id != null ? Number(user.id) : 0;
    const tid = formik.values?.trader_id != null ? Number(formik.values.trader_id) : 0;
    if (uid > 0 && tid > 0 && uid === tid) return true;
    const supervisorIds = new Set((formik.values?.supervisor_user_ids || []).map((id) => Number(id)));
    return uid > 0 && supervisorIds.has(uid);
  }, [user?.id, formik.values?.trader_id, formik.values?.supervisor_user_ids]);

  const updateExecutionUser = useCallback(async (executionUserId) => {
    if (!formik.values?.id) return null;
    const resp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/execution`, {
      execution_user_id: executionUserId || null,
    }, true);
    return resp?.data || null;
  }, [formik.values?.id]);

  const updateDeadline = useCallback(async (deadline) => {
    if (!formik.values?.id) return null;
    const resp = await postRequest(`/api/customer_evaluations/my_customers/${formik.values.id}/deadline`, {
      deadline: deadline || null,
    }, true);
    return resp?.data || null;
  }, [formik.values?.id]);

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(() => {
    const buildFetchRecord = () => async (direction, currentId) => {
      setDialogText?.(t('Common.LoadingData'));
      setVisible?.(true);
      try {
        const params = new URLSearchParams({ direction });
        if (currentId) params.set('id', String(currentId));
        const filters = Array.isArray(navigationContext?.filters) ? navigationContext.filters : [];
        const sorters = Array.isArray(navigationContext?.sorters) ? navigationContext.sorters : [];
        const toolbarSearch = String(navigationContext?.toolbarSearch ?? '').trim();
        filters.forEach((f) => {
          const field = String(f?.field ?? '').trim();
          const value = f?.value;
          if (!field || value == null || String(value).trim() === '') return;
          params.set(`filter_${field}`, String(value));
        });
        if (sorters.length > 0) params.set('sorters', JSON.stringify(sorters));
        if (toolbarSearch !== '') params.set('toolbar_search', toolbarSearch);
        const resp = await getRequest(`/api/customer_evaluations/central_db/navigate?${params.toString()}`);
        const payload = resp?.data ?? resp ?? null;
        if (payload && typeof payload === 'object' && payload.id) return payload;
        return payload?.data ?? null;
      } finally {
        setVisible?.(false);
      }
    };
    return createAsyncNavigateHandlers(
      buildFetchRecord(),
      (row) => {
        formik.setValues(normalizeInitialValues(row));
        setFormType('edit');
      },
      () => formik.values?.id ?? companyRow?.id ?? null
    );
  }, [companyRow?.id, formik, navigationContext, setDialogText, setVisible, t]);

  function openAddNew() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerCentralDatabaseAdd.New'), translationKey: 'CustomerCentralDatabaseAdd.New', legacyLabel: t('CustomerCentralDatabaseAdd.New'),
      page: PAGE_ADD_KEY,
      props: { companyRow: null, type: 'add' },
      mask: MASK_ADD,
    });
  }

  function copyCurrent() {
    dispatch({
      type: 'create_new_tab',
      name: t('CustomerCentralDatabaseAdd.New'), translationKey: 'CustomerCentralDatabaseAdd.New', legacyLabel: t('CustomerCentralDatabaseAdd.New'),
      page: PAGE_ADD_KEY,
      props: { companyRow: { ...formik.values, id: null, comments: [], mentions: [] }, type: 'add' },
      mask: MASK_ADD,
    });
  }

  const selectedPartyMode = formik.values?.party_mode || 'none';
  const onTypesChange = (e) => {
    // Type-of-partner options are visible but frozen in CRM central database.
    return;
  };
  const onPartyTypeCheckboxChange = (mode, checked) => {
    const nextMode = checked ? mode : 'none';
    formik.setFieldValue('party_mode', nextMode);
  };
  const traderOptions = useMemo(() => {
    const currentId = Number(formik.values?.trader_id || 0) || null;
    let options = [...traders];
    const hasCurrent = currentId && options.some((x) => Number(x.id) === currentId);
    if (!hasCurrent && currentId) {
      return [{ id: currentId, name: formik.values?.trader_name || `#${currentId}` }, ...options];
    }
    return options;
  }, [formik.values?.trader_id, formik.values?.trader_name, traders]);
  const disableSave = formType === 'add' ? !canAdd : !canEdit;
  const scrollIcons = !canEdit;

  useEffect(() => {
    const id = Number(companyRow?.id || 0);
    if (formType !== 'edit' || !id) return;
    let active = true;
    setDetailActivityLoading(true);
    (async () => {
      try {
        const row = await fetchCentralDetail(id);
        if (!active || !row) return;
        formik.setValues(normalizeInitialValues(row));
      } catch (e) {
        if (active) toast?.current?.show({ severity: 'error', summary: t('Common.Error'), detail: e?.message || t('Common.LoadFailed'), life: 3000 });
      } finally {
        if (active) setDetailActivityLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, companyRow?.id, fetchCentralDetail, t, toast]);

  const submitWithoutClose = () => {
    setCloseTab(false);
    submitRef.current?.click();
  };
  const submitWithClose = () => {
    setCloseTab(true);
    submitRef.current?.click();
  };
  return (
    <form onSubmit={formik.handleSubmit} className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="w-full h-10 flex flex-row bg-[#333] shrink-0">
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={disableSave} className="disabled:opacity-25" onClick={submitWithClose}><SaveAndCloseIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={disableSave} className="disabled:opacity-25" onClick={submitWithoutClose}><SaveIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={!canAdd} className="disabled:opacity-25" onClick={openAddNew}><AddIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" disabled={!canAdd} className="disabled:opacity-25" onClick={copyCurrent}><CopyIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={scrollIcons} onClick={scrollToFirst}><BackToFirstIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={scrollIcons} onClick={scrollUp}><BackIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={scrollIcons} onClick={scrollDown}><ForwardIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" className="disabled:opacity-25" disabled={scrollIcons} onClick={scrollToLast}><ForwardToLastIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><VerticalLineIcon /></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => openFavoriteDialog?.(t('CustomerCentralDatabaseAdd.FavoriteTitle'), PAGE_ADD_KEY)}><FavoriteIcon /></button></div>
        <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center"><button type="button" onClick={() => setFastView?.(PAGE_ADD_KEY)}><PriceIcon /></button></div>
      </div>
      <div className="relative flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-auto">
        <div className="w-full px-1 py-1 mb-2 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
            <h1 className="text-3xl font-semibold text-left text-zinc-900">
              {t('CustomerCentralDatabaseAdd.Title')}
            </h1>
            {formType === 'edit' && <div className="text-md font-semibold text-gray-800 mt-1">{t('Common.RecordIdLabel')}: {formik.values.id}</div>}
            <div className="mt-4">
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Name')}</label>
              <input name="name" value={formik.values.name} onChange={formik.handleChange} required className="block w-full px-4 py-2 mt-2 border rounded-md" />
              {formik.errors.name ? <div className="text-red-600 text-sm">{formik.errors.name}</div> : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 md:gap-6 gap-4 mt-4">
              <div className="md:col-span-4 min-w-0">
                <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.DefinedId')}</label>
                <input name="defined_id" value={formik.values.defined_id} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md bg-white" />
                <p className="text-xs text-gray-500 mt-1">{t('BusinessPartnerAdd.DefinedIdHint')}</p>
              </div>
              <div className="md:col-span-8 min-w-0">
                <label className="block text-md font-semibold text-gray-800">{t('CustomerCentralDatabase.Label.PartnerType')}</label>
                <div className="grid grid-cols-2 py-2 bg-white border rounded-md mt-2 px-3">
                  <div className="flex align-items-center">
                    <Checkbox name="types" value="supplier" onChange={onTypesChange} checked={formik.values.types?.includes('supplier')} disabled className="p-button-sm p-button-text pointer-events-none" />
                    <label htmlFor="supplier" className="ml-2">{t('BusinessPartnerAdd.Type.Supplier')}</label>
                  </div>
                  <div className="flex align-items-center">
                    <Checkbox name="types" value="calibration_company" onChange={onTypesChange} checked={formik.values.types?.includes('calibration_company')} disabled className="p-button-sm p-button-text pointer-events-none" />
                    <label htmlFor="calibration_company" className="ml-2">{t('BusinessPartnerAdd.Type.CalibrationCompany')}</label>
                  </div>
                  <div className="flex align-items-center">
                    <Checkbox name="types" value="training_company" onChange={onTypesChange} checked={formik.values.types?.includes('training_company')} disabled className="p-button-sm p-button-text pointer-events-none" />
                    <label htmlFor="training_company" className="ml-2">{t('BusinessPartnerAdd.Type.TrainingCompany')}</label>
                  </div>
                  <div className="flex align-items-center">
                    <Checkbox name="types" value="service_company" onChange={onTypesChange} checked={formik.values.types?.includes('service_company')} disabled className="p-button-sm p-button-text pointer-events-none" />
                    <label htmlFor="service_company" className="ml-2">{t('BusinessPartnerAdd.Type.ServiceCompany')}</label>
                  </div>
                  <div className="flex align-items-center">
                    <Checkbox
                      name="party_mode_customer"
                      onChange={(e) => onPartyTypeCheckboxChange('customer', e.checked)}
                      checked={selectedPartyMode === 'customer'}
                      className="p-button-sm p-button-text"
                      disabled={!canEdit}
                    />
                    <label htmlFor="party_mode_customer" className="ml-2">{t('BusinessPartnerAdd.Type.Customer')}</label>
                  </div>
                  <div className="flex align-items-center">
                    <Checkbox
                      name="party_mode_client"
                      onChange={(e) => onPartyTypeCheckboxChange('client', e.checked)}
                      checked={selectedPartyMode === 'client'}
                      className="p-button-sm p-button-text"
                      disabled={!canEdit}
                    />
                    <label htmlFor="party_mode_client" className="ml-2">{t('BusinessPartnerAdd.Type.Client')}</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-3 bg-white border rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-12 md:gap-3 gap-2">
                <div className="md:col-span-4 min-w-0">
                  <div>
                    <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.CustomerState')}</label>
                    <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                      <div className="flex align-items-center">
                        <Checkbox
                          inputId="customer_state_occupied"
                          checked={formik.values.customer_state === 'occupied'}
                          onChange={() => {}}
                          className="p-button-sm p-button-text pointer-events-none"
                        />
                        <label htmlFor="customer_state_occupied" className="ml-2">{t('CustomerCentralDatabase.State.Occupied')}</label>
                      </div>
                      <div className="flex align-items-center">
                        <Checkbox
                          inputId="customer_state_free"
                          checked={formik.values.customer_state !== 'occupied'}
                          onChange={() => {}}
                          className="p-button-sm p-button-text pointer-events-none"
                        />
                        <label htmlFor="customer_state_free" className="ml-2">{t('CustomerCentralDatabase.State.Free')}</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-8 min-w-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:pl-1">
                    <div className="min-w-0">
                      <label className="block text-md font-semibold text-gray-800">{t('CustomerMyCustomersDetail.Label.CustomerType')}</label>
                      <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                        {customerTypeOptions.map((opt) => (
                          <div key={`ro-customer-${opt.value}`} className="flex align-items-center">
                            <Checkbox
                              inputId={`ro_customer_type_${opt.value}`}
                              checked={formik.values.customer_type === opt.value}
                              onChange={() => {}}
                              className="p-button-sm p-button-text pointer-events-none"
                            />
                            <label htmlFor={`ro_customer_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="min-w-0 sm:pl-1">
                      <label className="block text-md font-semibold text-gray-800">{t('CustomerMyCustomersDetail.Label.ClientType')}</label>
                      <div className="mt-2 p-2 bg-white border rounded-md grid grid-cols-1 gap-1">
                        {clientTypeOptions.map((opt) => (
                          <div key={`ro-client-${opt.value}`} className="flex align-items-center">
                            <Checkbox
                              inputId={`ro_client_type_${opt.value}`}
                              checked={formik.values.client_type === opt.value}
                              onChange={() => {}}
                              className="p-button-sm p-button-text pointer-events-none"
                            />
                            <label htmlFor={`ro_client_type_${opt.value}`} className="ml-2">{customerClientTypeDisplayLabel(opt.label, locale)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.ReadOnly.Trader')}</label>
                <select
                  className="block w-full h-10 px-3 py-2 mt-2 border rounded-md bg-white"
                  value={formik.values.trader_id || ''}
                  onChange={(e) => {
                    const value = Number(e.target.value || 0) || null;
                    const trader = traders.find((x) => Number(x.id) === Number(value));
                    formik.setValues((prev) => ({ ...prev, trader_id: value, trader_name: trader?.name || '-' }));
                  }}
                  disabled={!canEdit}
                >
                  <option value="">{t('CustomerCentralDatabaseAdd.SelectTrader')}</option>
                  {traderOptions.map((trader) => (
                    <option key={trader.id} value={trader.id}>{trader.name}</option>
                  ))}
                </select>
              </div>
            </div>
                </div>
                <div className="w-auto p-6 m-1 bg-gray-100 rounded-md shadow-md">
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.ContactPerson')}</label>
              <input name="contact_person" value={formik.values.contact_person} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
              <p className="text-xs text-gray-500 mt-1">{t('CustomerMyCustomersDetail.ContactPotentialClientHint')}</p>
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Country')}</label>
              <Select
                className="block w-full h-10"
                showSearch={true}
                optionFilterProp="data-label"
                value={formik.values.country}
                onChange={(value) => formik.setFieldValue('country', value)}
              >
                <Select.Option value="">{t('BusinessPartnerAdd.SelectCountry')}</Select.Option>
                {countryOptions.map((country) => (
                  <Select.Option key={country.value} value={country.value} data-label={country.label}>{country.label}</Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Address')}</label>
              <input name="address" value={formik.values.address} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Phone')}</label>
              <input name="tel_number" value={formik.values.tel_number} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Email')}</label>
              <input name="email" value={formik.values.email} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.WebAddress')}</label>
              <input name="web_address" value={formik.values.web_address} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-md font-semibold text-gray-800">{t('BusinessPartnerAdd.Label.Note')}</label>
              <textarea name="description" value={formik.values.description} onChange={formik.handleChange} className="block w-full px-4 py-2 mt-2 border rounded-md" />
            </div>
                </div>
                <CustomerBusinessStatusPanel
                  customerId={formik.values?.id}
                  comments={formik.values?.comments || []}
                  mentions={formik.values?.mentions || []}
                  executionUserId={formik.values?.execution_user_id}
                  deadline={formik.values?.deadline}
                  deadlineReminderCommentId={formik.values?.deadline_reminder_comment_id}
                  usersForMentions={usersForMentions}
                  loading={detailActivityLoading}
                  disabled={false}
                  canManageInterestedUsers={canManageInterestedUsers}
                  canRemoveInterestedUsers={canRemoveInterestedUsers}
                  afterSaveHint={t('CustomerCentralDatabaseAdd.CrmAfterSaveHint')}
                  title={t('CustomerCentralDatabaseAdd.BusinessCustomerStateTitle')}
                  intro={t('CustomerCentralDatabaseAdd.BusinessCustomerStateIntro')}
                  onDetailUpdated={mergeDetailIntoFormik}
                  onChangeExecutionUser={updateExecutionUser}
                  onChangeDeadline={updateDeadline}
                />
              </div>
            </div>
      </div>
      <button type="submit" ref={submitRef} className="hidden" />
    </form>
  );
}
